/* eslint-disable */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Skeleton,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { CustomTable, TablePagination } from 'Shared/Table';
import useNetworkLoader from 'hooks/useNetworkLoader';
import useDebounce from 'hooks/useDebounce';
import toast from 'Utils/toast';
import ConfirmDialog from 'Shared/ConfirmDialog';
import Password from '../Password/Password';
import { getUsers, toggleClientStatus, getClientAccounts } from '../UsersService';
import { StatusBadge, getMfaStatus, subUserColumns } from './constants';
import ClientActionsMenu from './ClientActionsMenu';

/* ── Main component ──────────────────────────────────────── */
const UserList = ({ filters, update }) => {
  const { callApi } = useNetworkLoader();
  const [usersData, setUsersData] = useState({ content: [], totalElements: 0 });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [expandedRows, setExpandedRows] = useState({});
  const [subUsersMap, setSubUsersMap] = useState({});
  const [loadingSubUsers, setLoadingSubUsers] = useState({});
  const [userForPassword, setUserForPassword] = useState(null);
  const [statusConfirmDialog, setStatusConfirmDialog] = useState({ open: false, client: null });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [loading, setLoading] = useState(true);

  const debouncedFilters = useDebounce(filters, 500);

  const loadUsers = useCallback(() => {
    setLoading(true);
    callApi(
      () => getUsers({ page: page - 1, size: pageSize }, debouncedFilters),
      (data) => { setUsersData(data); setLoading(false); },
    );
  }, [page, pageSize, debouncedFilters, refreshTrigger, update]);

  useEffect(() => { loadUsers(); }, [loadUsers]);
  useEffect(() => { setPage(1); }, [debouncedFilters]);

  // Re-fetch expanded sub-users when the list is refreshed (new user created, status toggled, etc.)
  useEffect(() => {
    Object.keys(expandedRows).forEach((code) => {
      if (expandedRows[code]) loadSubUsers(code);
    });
  }, [update, refreshTrigger]);

  const loadSubUsers = useCallback((clientCode) => {
    setLoadingSubUsers((prev) => ({ ...prev, [clientCode]: true }));
    callApi(
      () => getClientAccounts(clientCode),
      (accounts) => {
        const mapped = (accounts || []).map((a) => ({
          id: a.id,
          username: a.username,
          enabled: a.enabled,
          errors: a.errors || 0,
          mfa: getMfaStatus(a),
        }));
        setSubUsersMap((prev) => ({ ...prev, [clientCode]: mapped }));
        setLoadingSubUsers((prev) => ({ ...prev, [clientCode]: false }));
      },
    );
  }, [callApi]);

  const toggleRow = (code) => {
    const willExpand = !expandedRows[code];
    setExpandedRows((prev) => ({ ...prev, [code]: willExpand }));
    if (willExpand && !subUsersMap[code]) {
      loadSubUsers(code);
    }
  };

  // Listen for sub-user action events (MFA reset, disable, delete) to refresh data
  useEffect(() => {
    const handleRefresh = () => {
      Object.keys(expandedRows).forEach((code) => {
        if (expandedRows[code]) loadSubUsers(code);
      });
      setRefreshTrigger((p) => p + 1);
    };
    window.addEventListener('subusers-refresh', handleRefresh);
    return () => window.removeEventListener('subusers-refresh', handleRefresh);
  }, [expandedRows, loadSubUsers]);

  const handleConfirmStatusToggle = async () => {
    const { client } = statusConfirmDialog;
    if (!client) return;
    try {
      await toggleClientStatus(client.code);
      toast.success('Statut du client modifié avec succès');
      setRefreshTrigger((p) => p + 1);
      if (expandedRows[client.code]) loadSubUsers(client.code);
    } catch (e) {
      toast.error(e?.response?.data?.statusLabel || 'Erreur lors de la modification du statut');
    }
  };

  const total = usersData?.totalElements || 0;
  const rows = usersData?.content || [];

  return (
    <>
      {userForPassword && (
        <Password
          updateList={setUsersData}
          handleClose={() => setUserForPassword(null)}
          user={userForPassword}
        />
      )}

      {statusConfirmDialog.open && (
        <ConfirmDialog
          title={statusConfirmDialog.client?.isEnabled ? 'Désactiver le client ?' : 'Activer le client ?'}
          content={
            statusConfirmDialog.client?.isEnabled
              ? `Vous allez passer ce client au statut Inactif.\nIl n'aura plus accès aux services ni aux opérations.`
              : `Vous allez passer ce client au statut Actif.\nIl aura de nouveau accès aux services et aux opérations.`
          }
          handleClose={() => setStatusConfirmDialog({ open: false, client: null })}
          handleClick={() => handleConfirmStatusToggle()}
        />
      )}

      {/* Main table container */}
      <Box sx={{ backgroundColor: '#fff', border: '1px solid #D1D5DB', borderRadius: '8px', overflow: 'hidden' }}>
        <TableContainer>
          <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
            {/* Header */}
            <TableHead>
              <TableRow sx={{ backgroundColor: '#FFFFFF' }}>
                {[
                  { label: 'Code Client', width: '24%' },
                  { label: 'Nom Client', width: '24%' },
                  { label: 'Statut', width: '24%' },
                  { label: 'Utilisateurs', width: '24%', sortable: true },
                  { label: 'Action', width: '6%' },
                ].map((col) => (
                  <TableCell
                    key={col.label}
                    sx={{
                      fontWeight: 600, fontSize: '13px', color: '#385576',
                      letterSpacing: '0.4px', textTransform: 'uppercase',
                      borderBottom: '1px solid #D1D5DB', py: '10px', px: '12px',
                      width: col.width, whiteSpace: 'nowrap',
                    }}
                  >
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                [...Array(pageSize)].map((_, idx) => (
                  <TableRow key={idx} sx={{ backgroundColor: idx % 2 === 0 ? '#FFFFFF' : '#F8FAFC' }}>
                    <TableCell sx={{ py: '10px', px: '12px', borderBottom: 'none' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Skeleton variant="circular" width={16} height={16} />
                        <Skeleton variant="text" width={100} height={22} />
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: '10px', px: '12px', borderBottom: 'none' }}><Skeleton variant="text" width={140} height={22} /></TableCell>
                    <TableCell sx={{ py: '10px', px: '12px', borderBottom: 'none' }}><Skeleton variant="rectangular" width={90} height={28} sx={{ borderRadius: '20px' }} /></TableCell>
                    <TableCell sx={{ py: '10px', px: '12px', borderBottom: 'none' }}><Skeleton variant="text" width={110} height={22} /></TableCell>
                    <TableCell sx={{ py: '10px', px: '12px', borderBottom: 'none' }}><Skeleton variant="circular" width={28} height={28} /></TableCell>
                  </TableRow>
                ))
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} sx={{ textAlign: 'center', py: '60px', color: '#8F8F8F', fontSize: '14px' }}>
                    Aucun utilisateur trouvé
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row, gi) => {
                  const isExpanded = Boolean(expandedRows[row.code]);
                  const subUsers = subUsersMap[row.code] || [];

                  return (
                    <React.Fragment key={row.code || gi}>
                      {/* Parent row */}
                      <TableRow
                        onClick={() => toggleRow(row.code)}
                        sx={{
                          backgroundColor: gi % 2 === 0 ? '#FFFFFF' : '#F8FAFC',
                          borderLeft: isExpanded ? '3px solid #007BC6' : '3px solid transparent',
                          borderTop: gi > 0 ? '1px solid #E2E8F0' : 'none',
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: '#F1F5F9' },
                          transition: 'background-color 0.15s',
                        }}
                      >
                        {/* Code client + chevron */}
                        <TableCell sx={{ py: '10px', px: '12px', borderBottom: 'none' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            {isExpanded
                              ? <KeyboardArrowDownIcon sx={{ fontSize: 16, color: '#007BC6' }} />
                              : <KeyboardArrowRightIcon sx={{ fontSize: 16, color: '#007BC6' }} />}
                            <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#000000' }}>
                              {row.code || '—'}
                            </Typography>
                          </Box>
                        </TableCell>

                        {/* Nom client */}
                        <TableCell sx={{ py: '10px', px: '12px', borderBottom: 'none' }}>
                          <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#000000' }}>
                            {row.name || '—'}
                          </Typography>
                        </TableCell>

                        {/* Statut */}
                        <TableCell sx={{ py: '10px', px: '12px', borderBottom: 'none' }}>
                          <StatusBadge enabled={row.enabledUserCount > 0} hasAccount={row.hasAccount} />
                        </TableCell>

                        {/* Utilisateurs count */}
                        <TableCell sx={{ py: '10px', px: '12px', borderBottom: 'none' }}>
                          <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#000000' }}>
                            {row.userCount ?? 0} {(row.userCount ?? 0) <= 1 ? 'utilisateur' : 'utilisateurs'}
                          </Typography>
                        </TableCell>

                        {/* Action */}
                        <TableCell sx={{ py: '6px', px: '12px', borderBottom: 'none' }} onClick={(e) => e.stopPropagation()}>
                          <ClientActionsMenu
                            row={{ ...row, isEnabled: row.enabledUserCount > 0 }}
                            onToggleStatus={() => setStatusConfirmDialog({ open: true, client: { ...row, isEnabled: row.enabledUserCount > 0 } })}
                            onRefresh={() => setRefreshTrigger((p) => p + 1)}
                          />
                        </TableCell>
                      </TableRow>

                      {/* Expanded sub-table */}
                      {isExpanded && (
                        <TableRow sx={{ backgroundColor: gi % 2 === 0 ? '#FFFFFF' : '#F8FAFC' }}>
                          <TableCell
                            colSpan={5}
                            sx={{ py: 0, px: '16px', borderBottom: '1px solid #E2E8F0', borderLeft: '3px solid #007BC6' }}
                          >
                            <Box sx={{ mx: 0, pb: '16px', mt: '8px' }}>
                              <CustomTable
                                columns={subUserColumns}
                                data={subUsers}
                                loading={Boolean(loadingSubUsers[row.code])}
                                pageSize={3}
                                rowId="id"
                                emptyMessage="Aucun utilisateur"
                                sx={{
                                  border: 'none',
                                  borderRadius: '8px',
                                  '& table': { borderCollapse: 'separate', borderSpacing: '0' },
                                  '& thead': { backgroundColor: gi % 2 === 0 ? '#FFFFFF' : '#F8FAFC' },
                                  '& thead th': {
                                    borderBottom: 'none',
                                    color: '#385576',
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.4px',
                                  },
                                  '& tbody tr': { backgroundColor: gi % 2 === 0 ? '#F8FAFC' : '#FFFFFF', height: '52px' },
                                  '& tbody tr td': { borderBottom: '1px solid #D1D5DB', borderTop: '1px solid #D1D5DB' },
                                  '& tbody tr td:first-of-type': { borderLeft: '1px solid #D1D5DB' },
                                  '& tbody tr td:last-of-type': { borderRight: '1px solid #D1D5DB' },
                                  '& tbody tr:not(:first-of-type) td': { borderTop: 'none' },
                                  '& tbody tr:first-of-type td:first-of-type': { borderRadius: '8px 0 0 0' },
                                  '& tbody tr:first-of-type td:last-of-type': { borderRadius: '0 8px 0 0' },
                                  '& tbody tr:last-of-type td:first-of-type': { borderRadius: '0 0 0 8px' },
                                  '& tbody tr:last-of-type td:last-of-type': { borderRadius: '0 0 8px 0' },
                                }}
                              />
                            </Box>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <Box sx={{ borderTop: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', backgroundColor: rows.length % 2 === 1 ? '#FFFFFF' : '#F8FAFC' }}>
          <TablePagination
            page={page}
            pageSize={pageSize}
            total={total}
            onChange={(newPage) => setPage(newPage)}
            pageSizeOptions={[10, 25, 50]}
            onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
            sx={{ width: '100%' }}
          />
        </Box>
      </Box>
    </>
  );
};

export default UserList;
